import React, { FunctionComponent } from 'react';
import { useTranslation } from '@wix/wix-i18n-config';
import { Box, Text, TextButton } from '@wix/design-system';
import ChevronRightIcon from 'wix-ui-icons-common/ChevronRight';
import ChevronLeftIcon from 'wix-ui-icons-common/ChevronLeft';
import { useRouter } from 'next/router';
import { helpCenterPageClick } from '@wix/bi-logger-customer-care-data/v2';
import { ArticleType, Category } from '@wix/answers-api';
import { DATA_HOOKS } from '../../../dataHooks';
import { BI } from '../../../constants';
import { useBI } from '../../../hooks/useBI';
import { ArticleSearchResultItem } from '../../../types';
import { isRTL } from '../../../utils/internationalization';
import css from './index.module.scss';
type SearchResultCategoriesProps = {
  categoriesPaths: Record<string, Category[]>;
  searchResult: ArticleSearchResultItem;
  searchResultIndex: string;
};

/**
 * @deprecated use `components/SearchResults/SearchResultCategories/index.tsx` instead
 */

const DeprecatedSearchResultCategories: FunctionComponent<
  SearchResultCategoriesProps
> = ({
  categoriesPaths,
  searchResult,
  searchResultIndex,
}: SearchResultCategoriesProps) => {
  const { sendBIEvent } = useBI();
  const { t } = useTranslation();
  const { locale } = useRouter();
  const isRTLLanguage = isRTL(locale);
  const categories = categoriesPaths[searchResult.categoryId];
  const onCategoryClick = async (category: Category, idx: number) => {
    await sendBIEvent(
      helpCenterPageClick({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_item_type:
          idx === 0
            ? BI.CLICKED_ITEM_TYPES.CATEGORY_SELECTION
            : BI.CLICKED_ITEM_TYPES.SUB_CATEGORY_SELECTION,
        clicked_item_id: category.id,
        clicked_item_order: searchResultIndex,
        clicked_text: category.name,
        clicked_url: `${category.url}`,
      })
    );
  };

  const getArticleTypeTitle = (articleType: ArticleType): string => {
    switch (articleType) {
      case ArticleType.FEATURE_REQUEST:
        return t('feature.request.title');
      case ArticleType.KNOWN_ISSUE:
        return t('known.issue.title');
      case ArticleType.ARTICLE:
      default:
        return t('article.title');
    }
  };

  return (
    <Box verticalAlign="middle" className={css.searchResultCategories}>
      {categories.filter((cat) => !!cat).map((category, idx) => (
        <>
          <TextButton
            key={category.id}
            as="a"
            href={`/${locale}${category.uri}`}
            size="small"
            ellipsis
            dataHook={
              idx === 0
                ? DATA_HOOKS.SEARCH_RESULT_CATEGORIES_CATEGORY
                : DATA_HOOKS.SEARCH_RESULT_CATEGORIES_SUB_CATEGORY
            }
            onClick={() => onCategoryClick(category, idx)}
            className={css.categoryLink}
            type="link"
          >
            {category.name}
          </TextButton>
          {idx < categories.length - 1 ? (
            isRTLLanguage ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )
          ) : null}
        </>
      ))}
      <Box className={css.searchResultType} verticalAlign="middle">
        <>{isRTLLanguage ? <ChevronLeftIcon /> : <ChevronRightIcon />}</>
        <Text
          size="small"
          ellipsis
          dataHook={DATA_HOOKS.SEARCH_RESULT_CATEGORIES_TYPE}
        >
          {getArticleTypeTitle(searchResult.type)}
        </Text>
      </Box>
    </Box>
  );
};

export default DeprecatedSearchResultCategories;
